// exclude-from-index
import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Spacer from '../Spacer';
import Select from '../Select';
import { StyledButton, StyledSingleDateSelect } from './SingleDateSelect.styles.js';

import SVGIcon from '../../CustomerSVGIcon';

class SingleDateSelect extends PureComponent {
  static propTypes = {
    error: PropTypes.bool,
    errorText: PropTypes.string,
    /**
     * Preset date
     */
    defaultDate: PropTypes.string,
    /**
     * disables the use of this component, and updates the styles
     */
    disabled: PropTypes.bool,
    /**
     * name attribute applied to select elements
     */
    name: PropTypes.string,
    /**
     *  callback function when year, month, day has changed. Returns number value.
     */
    onChange: PropTypes.func.isRequired,
    /**
     *  Start of the year range eg: 1920
     */
    startYear: PropTypes.number,
    /**
     *  End of the year range eg: 2020
     */
    endYear: PropTypes.number,
    /**
     * Classname for styling purposes
     */
    className: PropTypes.string,
    /**
     * sets the width to 100% and stacks the selects
     * */
    stack: PropTypes.bool,
    /**
     * Determines if the payment code is being made through booking flow
     * */
    bookingFlow: PropTypes.bool,
    /**
     * Tells us the property timezone of the booking
     * */
    propertyTimeZone: PropTypes.string
  };

  static defaultProps = {
    bookingFlow: false,
    propertyTimeZone: '',
    defaultDate: '',
    disabled: false,
    error: false,
    errorText: 'Please select a year, month and day',
    name: undefined,
    startYear: new Date().getFullYear() - 100,
    endYear: new Date().getFullYear(),
    className: '',
    stack: false
  };

  /**
   * @description - Returns the proper number of days for a selected month (including leap years)
   * @param {number} year - The year of target date
   * @param {number} month - The month of target date
   * @return {number} - 28
   */
  static daysInMonth = (year, month) => new Date(year, month, 0).getDate();

  /**
   * @description - Creates an array of day objects {label, value} to be used with select components
   * @param {number} numOfDays - The number of days to create
   * @return {array} - [{label: '1', value: '1'}]
   */
  static getDayData = (propertyTimeZone, bookingFlow, yearValue, monthValue, numOfDays = 0) => {
    const dayData = [{ label: '', value: '' }];

    // If the year and month selected are current month and year then we render the days left in the current month
    const isSelectedYearTheCurrentYear = yearValue === moment.tz(propertyTimeZone).format('YYYY');
    const isSelectedMonthTheCurrentMonth = monthValue === moment.tz(propertyTimeZone).format('M');
    const currentDayOfMonth = parseInt(moment.tz(propertyTimeZone).format('D'));
    const currentDay =
      bookingFlow && isSelectedYearTheCurrentYear && isSelectedMonthTheCurrentMonth ? currentDayOfMonth : 0;
    for (let i = currentDay; i < numOfDays; i += 1) {
      const label = (parseInt(i, 10) + 1).toString();
      const value = (i + 1).toString();

      dayData.push({ label, value });
    }

    return dayData;
  };

  /**
   * @description - Creates an array of month objects {label, value} to be used with select components
   * @return {array} - [{label: 'January', value: '1'}]
   */
  static getMonthData = (propertyTimeZone, bookingFlow, yearValue) => {
    const monthData = [{ label: '', value: '' }];
    const monthLabels = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];
    const isSelectedYearTheCurrentYear = yearValue === moment.tz(propertyTimeZone).format('YYYY');
    const getCurrentMonth = parseInt(moment.tz(propertyTimeZone).format('M')) - 1;
    const currentMonth = bookingFlow && isSelectedYearTheCurrentYear ? getCurrentMonth : 0;
    for (let i = currentMonth; i < 12; i += 1) {
      const label = monthLabels[i];
      const value = (i + 1).toString();
      monthData.push({ label, value });
    }
    return monthData;
  };

  /**
   * @description - Creates an array of year objects {label, value} to be used with select components
   * @return {array} - [{label: '2019', value: '2019'}]
   */
  static getYearData = (startYear, endYear) => {
    const yearData = [{ label: '', value: '' }];
    const yearRange = endYear - startYear + 1;
    const firstDate = endYear;

    for (let i = 0; i < yearRange; i += 1) {
      const year = (firstDate - i).toString();

      yearData.push({ label: year, value: year });
    }

    return yearData;
  };

  constructor(props) {
    super(props);
    const { propertyTimeZone, bookingFlow } = props;
    // Parse year, month, day if defaultDate is provided
    const splitDate = props.defaultDate.split('-');
    const year = props.defaultDate && splitDate[0] ? splitDate[0].toString() : '';
    const month = props.defaultDate && splitDate[1] ? parseInt(splitDate[1], 10).toString() : '';
    const day = props.defaultDate && splitDate[2] ? parseInt(splitDate[2], 10).toString() : '';

    this.state = {
      dayData:
        year && month
          ? SingleDateSelect.getDayData(
              propertyTimeZone,
              bookingFlow,
              year,
              month,
              SingleDateSelect.daysInMonth(year, month)
            )
          : [],
      dayValue: day,
      disableDay: !month,
      disableMonth: !year,
      disableYear: props.disabled,
      monthData: SingleDateSelect.getMonthData(propertyTimeZone, bookingFlow, year),
      monthValue: month,
      yearData: SingleDateSelect.getYearData(props.startYear, props.endYear),
      yearValue: year
    };

    this.handleClearDate = this.handleClearDate.bind(this);
    this.handleDayChange = this.handleDayChange.bind(this);
    this.handleMonthChange = this.handleMonthChange.bind(this);
    this.handleYearChange = this.handleYearChange.bind(this);
  }

  /**
   * @description - Handles clearing all the select values
   */
  handleClearDate = () => {
    const { onChange } = this.props;

    onChange('');

    this.setState({ dayValue: '', disableDay: true, disableMonth: true, monthValue: '', yearValue: '' });
  };

  /**
   * @description - Handles updating state when day select has changed
   * @param {object} evt - Event object
   */
  handleDayChange = evt => {
    const { onChange } = this.props;
    const { yearValue, monthValue } = this.state;
    const value = evt.target.value.toString();
    const padMonth = monthValue.length > 1 ? monthValue : `0${monthValue}`;
    const padDay = value.length > 1 ? value : `0${value}`;
    const dateString = `${yearValue}-${padMonth}-${padDay}`;

    this.setState({ dayValue: value });

    onChange(dateString);
  };

  /**
   * @description - Handles updating state when month select has changed
   * @param {object} evt - Event object
   */
  handleMonthChange = evt => {
    const { onChange, bookingFlow, propertyTimeZone } = this.props;
    const { dayValue, yearValue } = this.state;
    const value = parseInt(evt.target.value, 10);
    const makeMonthValueString = value.toString();

    if (dayValue) {
      onChange('');
    }

    this.setState({
      dayData: SingleDateSelect.getDayData(
        propertyTimeZone,
        bookingFlow,
        yearValue,
        makeMonthValueString,
        SingleDateSelect.daysInMonth(yearValue, value)
      ),
      dayValue: '',
      disableDay: value < 1,
      monthValue: makeMonthValueString
    });
  };

  /**
   * @description - Handles updating state when year select has changed
   * @param {object} evt - Event object
   */
  handleYearChange = evt => {
    const { onChange, bookingFlow, propertyTimeZone } = this.props;
    const { dayValue } = this.state;
    const value = evt.target.value.toString();

    if (dayValue) {
      onChange('');
    }

    this.setState({
      dayValue: '',
      disableDay: true,
      disableMonth: !value,
      monthValue: '',
      yearValue: value,
      monthData: SingleDateSelect.getMonthData(propertyTimeZone, bookingFlow, value)
    });
  };

  render() {
    const { disabled, error, errorText, name, className, stack } = this.props;
    const {
      dayData,
      dayValue,
      disableDay,
      disableMonth,
      disableYear,
      monthData,
      monthValue,
      yearData,
      yearValue
    } = this.state;
    return (
      <FormControl error={error} fullWidth>
        <StyledSingleDateSelect stack={stack} className={className} disabled={disabled}>
          <div>
            <Select
              autoWidth={!stack}
              disabled={disabled || disableYear}
              error={!yearValue && !disabled && error}
              id={`${name}-year`}
              className="single-date-year-select"
              label="Year"
              name={`${name}Year`}
              onChange={this.handleYearChange}
              options={yearData}
              value={yearValue}
              native
              data-cy="sds-year"
            />
            {stack && <Spacer size="sm" />}
          </div>
          <div>
            <Select
              autoWidth={!stack}
              disabled={disabled || disableMonth}
              error={!monthValue && !disabled && !disableMonth && error}
              id={`${name}-month`}
              className="single-date-month-select"
              label="Month"
              name={`${name}Month`}
              onChange={this.handleMonthChange}
              options={monthData}
              value={monthValue}
              native
              data-cy="sds-month"
            />
            {stack && <Spacer size="sm" />}
          </div>
          <div>
            <Select
              autoWidth={!stack}
              disabled={disabled || disableDay}
              error={!dayValue && !disabled && !disableDay && error}
              id={`${name}-day`}
              className="single-date-day-select"
              label="Day"
              name={`${name}Day`}
              onChange={this.handleDayChange}
              options={dayData}
              value={dayValue}
              native
              data-cy="sds-day"
            />
            {stack && <Spacer size="sm" />}
          </div>
          {error && stack && <FormHelperText>{errorText}</FormHelperText>}
          {stack ? (
            <>
              <Spacer size="sm" />
              <StyledButton pill onClick={this.handleClearDate}>
                Clear Date <SVGIcon selectedIcon="clear-search" iconColor="#FFFFFF" onClick={this.handleClearDate} />
              </StyledButton>
              <Spacer size="sm" />
            </>
          ) : (
            <SVGIcon selectedIcon="clear-search" onClick={this.handleClearDate} />
          )}
        </StyledSingleDateSelect>
        {error && !stack && <FormHelperText> {errorText}</FormHelperText>}
      </FormControl>
    );
  }
}

export default SingleDateSelect;
